import { Texture, type Scene } from "@babylonjs/core";

export const SURFACE_TEXTURE_IDS = [
  "wood", "planks", "fabric", "leather", "metal", "sand", "ground", "rock", "snow",
  "rope", "bark", "leaf", "fine", "ice",
  "fortress-wood", "fortress-rock", "fortress-sand", "fortress-ground", "fortress-bark",
  "fortress-leaf", "fortress-fabric", "fortress-rope", "fortress-metal",
  "ship-sail", "ship-gilt",
  "character-wool", "character-knit", "character-hair", "character-skin", "character-leather",
] as const;
export type SurfaceTextureId = (typeof SURFACE_TEXTURE_IDS)[number];

type MapKind = "albedo" | "normal" | "orm";
const MAP_KINDS = ["albedo", "normal", "orm"] as const;

/** Requested taps per texel; clamped to the engine's reported maximum. */
const ANISOTROPY = 16;

export interface SurfaceTextures {
  readonly albedo: Texture;
  readonly normal: Texture;
  readonly orm: Texture;
}

// Page-owned decoded images survive scene disposal and WebGL context restoration.
const images = new Map<string, HTMLImageElement>();
let loading: Promise<void> | null = null;
let loaded = false;

function imageKey(id: SurfaceTextureId, kind: MapKind): string {
  return `${id}/${kind}.${kind === "albedo" ? "webp" : "png"}`;
}

async function loadImage(key: string): Promise<void> {
  if (images.has(key)) return;
  const image = new Image();
  image.src = new URL(`textures/${key}`, document.baseURI).href;
  try { await image.decode(); }
  catch (cause) { throw new Error(`surface map ${key}: image load/decode failed`, { cause }); }
  images.set(key, image);
}

/** Decode every required local map before any scene can request its GPU wrappers. */
export function loadSurfaceMaps(): Promise<void> {
  loading ??= (async () => {
    const results = await Promise.allSettled([
      ...SURFACE_TEXTURE_IDS.flatMap(id => MAP_KINDS.map(kind => loadImage(imageKey(id, kind)))),
      loadImage("smoke.png"),
    ]);
    const failure = results.find((result) => result.status === "rejected");
    if (failure?.status === "rejected") throw failure.reason;
    loaded = true;
  })().catch((error: unknown) => {
    // Keep successful decodes, but let the lobby's existing retry reload failures.
    loading = null;
    throw error;
  });
  return loading;
}

interface SceneTextures {
  maps: Map<SurfaceTextureId, SurfaceTextures>;
  normals: Map<SurfaceTextureId, Map<number, Texture>>;
}
const scenes = new WeakMap<Scene, SceneTextures>();

function sceneTextures(scene: Scene): SceneTextures {
  if (!loaded) throw new Error("loadSurfaceMaps() must resolve before creating surface materials");
  let cache = scenes.get(scene);
  if (!cache) {
    cache = { maps: new Map(), normals: new Map() };
    scenes.set(scene, cache);
  }
  return cache;
}

function createMap(scene: Scene, id: SurfaceTextureId, kind: MapKind): Texture {
  const key = imageKey(id, kind);
  const image = images.get(key);
  if (!image) throw new Error(`surface map ${key}: decoded image missing`);
  const texture = new Texture(image.src, scene, {
    buffer: image,
    deleteBuffer: false,
    noMipmap: false,
    invertY: false,
    samplingMode: Texture.TRILINEAR_SAMPLINGMODE,
    gammaSpace: kind === "albedo",
  });
  texture.name = `surface:${id}:${kind}`;
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  // Ground, sand, rock and deck are seen at grazing angles from the first-person
  // camera, where 4 taps leave the tiling maps visibly stretched. TRILINEAR
  // sampling above supplies the mip chain the extra taps need.
  texture.anisotropicFilteringLevel = Math.max(1, Math.min(ANISOTROPY, scene.getEngine().getCaps().maxAnisotropy));
  return texture;
}

/** Shared immutable base wrappers: callers must never change their scale or level. */
export function getSurfaceTextures(scene: Scene, id: SurfaceTextureId): SurfaceTextures {
  const cache = sceneTextures(scene);
  let maps = cache.maps.get(id);
  if (!maps) {
    maps = Object.freeze({
      albedo: createMap(scene, id, "albedo"),
      normal: createMap(scene, id, "normal"),
      orm: createMap(scene, id, "orm"),
    });
    cache.maps.set(id, maps);
  }
  return maps;
}

/** Profile-specific wrappers share the base normal's internal GPU texture. */
export function getSurfaceNormal(scene: Scene, id: SurfaceTextureId, strength: number): Texture {
  const cache = sceneTextures(scene);
  let levels = cache.normals.get(id);
  if (!levels) {
    levels = new Map();
    cache.normals.set(id, levels);
  }
  let normal = levels.get(strength);
  if (!normal) {
    // Texture.clone() reuses Babylon's URL/invertY/mipmap cache and retains its
    // image buffer. Only this lightweight wrapper's level differs from the base.
    normal = getSurfaceTextures(scene, id).normal.clone();
    normal.name = `surface:${id}:normal:${strength}`;
    normal.level = strength;
    levels.set(strength, normal);
  }
  return normal;
}

const smokeTextures = new WeakMap<Scene, Texture>();

/** The one original opacity sprite shares the same required-image preload gate. */
export function getSmokeTexture(scene: Scene): Texture {
  if (!loaded) throw new Error("loadSurfaceMaps() must resolve before creating smoke");
  let texture = smokeTextures.get(scene);
  if (!texture) {
    const image = images.get("smoke.png")!;
    texture = new Texture(image.src, scene, { buffer: image, deleteBuffer: false, invertY: false, gammaSpace: false });
    texture.hasAlpha = true;
    texture.wrapU = texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    smokeTextures.set(scene, texture);
  }
  return texture;
}
